import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient'; // Your configured Supabase client
// API_CONFIG is no longer needed for refresh interval here, but keep if DEV_FUND_ADDRESS might come from it.
// For now, assume DEV_FUND_ADDRESS is primarily for the Edge Function.
// The client-side will query for the specific address row.

const DEV_FUND_TARGET_ADDRESS = 'WmXhHCV6PjXjxJdSXPeC8e4PrY8qTQMBFg'; // The address we query for
const REFETCH_INTERVAL_MS = 1 * 60 * 1000; // Check every 1 minute
const MAX_DATA_AGE_MS = 3 * 60 * 1000; // Refetch if data is older than 3 minutes

// Interface for data coming from Supabase (snake_case)
interface DbDevFundDetails {
  address: string;
  balance: number | null; // Balance can be null if not found or error
  last_updated_at?: string | null;
}

export function useDevFundBalance() {
  const [balance, setBalance] = useState<number>(0); // Default to 0 or a sensible fallback
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState<number>(0);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      console.log('[useDevFundBalance] Background polling/refetching dev fund balance...');
    }
    setError(null);
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('dev_fund_details')
        .select('balance, last_updated_at') 
        .eq('address', DEV_FUND_TARGET_ADDRESS)
        .single<DbDevFundDetails>();

      if (dbError) {
        console.error('[useDevFundBalance] Error fetching data:', dbError);
        if (isInitialLoad) setBalance(0); 
        setError(new Error('Failed to fetch dev fund balance: ' + dbError.message));
      } else if (dbData && dbData.balance !== null) {
        setBalance(dbData.balance);
        setLastFetchTimestamp(Date.now());
        console.log('[useDevFundBalance] Fetched balance:', dbData.balance);
      } else {
        if (isInitialLoad) setBalance(0);
        console.warn(`[useDevFundBalance] Data not found or balance is null for ${DEV_FUND_TARGET_ADDRESS} via fetchData.`);
      }
    } catch (err) {
      console.error('[useDevFundBalance] Catch block error in fetchData:', err);
      if (isInitialLoad) setBalance(0);
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch and real-time subscription
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      // Initial fetch
      await fetchData(true);

      if (!mounted) return;

      // Set up real-time subscription
      channel = supabase
        .channel('public:dev_fund_details')
        .on(
          'postgres_changes',
          { 
            event: '*', 
            schema: 'public', 
            table: 'dev_fund_details',
            filter: `address=eq.${DEV_FUND_TARGET_ADDRESS}` // Only listen for updates to our specific address
          },
          (payload) => {
            if (!mounted) return;
            console.log('[useDevFundBalance] Realtime update received:', payload);
            
            // Handle different event types
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
              const newBalance = payload.new?.balance;
              if (newBalance !== undefined && newBalance !== null) {
                setBalance(newBalance);
                setLastFetchTimestamp(Date.now());
                console.log('[useDevFundBalance] Balance updated via realtime:', newBalance);
              }
            } else if (payload.eventType === 'DELETE') {
              console.warn('[useDevFundBalance] Dev fund record was deleted');
              setBalance(0);
            }
          }
        )
        .subscribe((status, err) => {
          if (!mounted) return;
          if (err) {
            console.error('[useDevFundBalance] Realtime subscription error:', err);
            // Don't set error state here as it might be too noisy
          } else if (status === 'SUBSCRIBED') {
            console.log('[useDevFundBalance] Successfully subscribed to dev_fund_details updates');
          }
        });
    };

    setupSubscription();

    return () => {
      mounted = false;
      if (channel) {
        // Use timeout to ensure cleanup happens after any pending operations
        const channelToRemove = channel;
        setTimeout(() => {
          supabase.removeChannel(channelToRemove).catch(err => 
            console.error('[useDevFundBalance] Failed to remove channel:', err)
          );
        }, 0);
      }
    };
  }, [fetchData]);

  // Periodic refetch if data is stale and tab is visible (as fallback)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) { 
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useDevFundBalance] Data is stale (>3min), refetching due to interval (polling/focus strategy)...');
          fetchData();
        }
      }
    }, REFETCH_INTERVAL_MS); 
    return () => clearInterval(intervalId);
  }, [lastFetchTimestamp, fetchData]);

  // Refetch on window focus if data is stale (as fallback)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useDevFundBalance] Tab focused and data is stale (>3min), refetching (polling/focus strategy)...');
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTimestamp, fetchData]);

  return { balance, loading, error };
}